import React from 'react';
import { useRef, useEffect } from 'react';
import ProductCard from './ProductCard';
import { Link } from 'react-router-dom';

const ProductShowcase = ({ title, products, subtitle, loading, badge, showSeeMore = true, showExclusiveBadge = true, seeMoreLink }) => {
  const trackRef = useRef(null);
  const copiesCount = Math.max(8, products ? products.length * 2 : 8);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !products || products.length === 0) return;

    let animationId;
    let position = 0;
    const speed = 0.5;

    const getSingleCopyWidth = () => {
      const totalChildren = track.children.length;
      const singleCount = Math.ceil(totalChildren / copiesCount);
      if (totalChildren === 0) return 0;
      const itemWidth = track.scrollWidth / totalChildren;
      return itemWidth * singleCount;
    };

    const step = () => {
      position += speed;
      const singleWidth = getSingleCopyWidth();
      if (singleWidth > 0 && position >= singleWidth) {
        position = position - singleWidth;
      }
      track.style.transform = `translateX(-${position}px)`;
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);

    const pause = () => cancelAnimationFrame(animationId);
    const resume = () => { animationId = requestAnimationFrame(step); };

    track.parentElement.addEventListener('mouseenter', pause);
    track.parentElement.addEventListener('mouseleave', resume);

    return () => {
      cancelAnimationFrame(animationId);
      track.parentElement.removeEventListener('mouseenter', pause);
      track.parentElement.removeEventListener('mouseleave', resume);
    };
  }, [products, copiesCount]);

  if (!loading && (!products || products.length === 0)) {
    return null;
  }

  return (
    <section className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold">{title}</h2>
            {subtitle && (
              <p className="text-gray-600 mt-2 text-sm">{subtitle}</p>
            )}
            <div className="w-16 h-0.5 bg-gold mx-auto mt-2"></div>
          </div>
          {!loading && showSeeMore && (
            <Link to={seeMoreLink || '/shop-all'} className="text-sm font-medium text-gold hover:brightness-90 transition-colors">
              See More →
            </Link>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 aspect-[3/4] rounded-xl mb-4"></div>
                <div className="bg-gray-200 h-4 rounded w-3/4 mb-2"></div>
                <div className="bg-gray-200 h-4 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="overflow-hidden w-full"
            style={{
              position: 'relative',
              maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
            }}
          >
            <div
              ref={trackRef}
              className="flex gap-4 md:gap-6 items-stretch"
              style={{ width: 'max-content', willChange: 'transform' }}
            >
              {Array.from({ length: copiesCount }).flatMap((_, copyIndex) =>
                (products || []).map((product, index) => (
                  <div
                    key={`${copyIndex}-${product.id || index}`}
                    className="flex-shrink-0"
                    style={{ width: '180px' }}
                  >
                    <ProductCard
                      product={product}
                      badge={badge}
                      isNew={product.is_new}
                      isExclusive={showExclusiveBadge ? undefined : false}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductShowcase;
